/**
 * Experience dates: the format theResume and theRecruit store, and the one reader both use.
 *
 * Stored like LinkedIn: a start month and year, and an end month and year unless the person still
 * works there ("I currently work here"). No day. A year on its own only appears in older or parsed
 * dates, which is why a month can be null.
 *
 * Keep in sync: theResume libs/utils/src/namespaces/experience-dates.ts and theRecruit
 * apps/server/src/central-resume/experience-dates.ts hold the same code, and
 * experience-dates.fixtures.json (the same file in both) holds the date formats seen in real
 * resumes. Both test suites run every fixture, so the two readers can't drift apart.
 */

/** A month (1-12, or null when only the year is known) and a year. */
export type MonthYear = { year: number; month: number | null };

/** Who wrote the dates: typed into a form, read from a resume, or added to an older record. */
export type DatesSource = "entered" | "parsed" | "backfill";

/** Stored on an experience entry as `dates`. */
export type ExperienceDates = {
  start: MonthYear | null;
  /** Null when the person still works there, or when no end date is given. */
  end: MonthYear | null;
  /** "I currently work here". */
  current: boolean;
  source: DatesSource;
};

/** How the end of a role is known once the whole work history is read. */
export type EndStatus = "given" | "current" | "inferred" | "missing";

export type ResolvedDates = ExperienceDates & {
  /**
   * given: an end date is stated. current: still works there. inferred: no end date, so the role
   * ends where the next one starts. missing: the latest role, with no end date; it may or may not
   * be current.
   */
  endStatus: EndStatus;
  /** "year" when the start or end gives only a year. */
  precision: "month" | "year";
};

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const MONTH_NAMES = MONTHS.map((m) => m[0].toUpperCase() + m.slice(1));
const ONGOING = /^(present|current|currently|now|ongoing|today|to date|till date|till now)$/i;

type Loose = { year: number | null; month: number | null };

/**
 * One date as written in a resume: "Jan 2020", "January 2020", "01/2020", "1-2020", "2020-01",
 * "2020-01-15", "2020", or a bare month ("March", year filled in by the range). Years outside
 * 1950..next year (typos like "June 202 3") are rejected rather than counted.
 */
function parseLoose(value: unknown, now: Date): Loose | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime())
      ? null
      : { year: value.getFullYear(), month: value.getMonth() + 1 };
  }
  const text = String(value)
    .trim()
    .toLowerCase()
    .replace(/\s*\(.*\)\s*$/, "") // "Jun 2021 (2 yrs 3 mos)"
    .replace(/\s*[·•|].*$/, "") // "Jun 2021 · 2 yrs"
    .trim();
  if (!text) return null;
  const validYear = (y: number) => y >= 1950 && y <= now.getFullYear() + 1;
  let m = text.match(/^(\d{4})-(\d{1,2})(?:-\d{1,2})?(?:t.*)?$/);
  if (m)
    return validYear(+m[1]) && +m[2] >= 1 && +m[2] <= 12 ? { year: +m[1], month: +m[2] } : null;
  m = text.match(/^(\d{1,2})[/.-](\d{4})$/);
  if (m)
    return validYear(+m[2]) && +m[1] >= 1 && +m[1] <= 12 ? { year: +m[2], month: +m[1] } : null;
  m = text.match(/^([a-z]{3,9})\.?,?\s*(\d{4})?$/);
  if (m) {
    const month = MONTHS.indexOf(m[1].slice(0, 3));
    // Not a month name ("Since 2019", "Summer 2019"): only the year, below.
    if (month >= 0) {
      if (!m[2]) return { year: null, month: month + 1 };
      return validYear(+m[2]) ? { year: +m[2], month: month + 1 } : null;
    }
  }
  m = text.match(/^(\d{4})$/);
  if (m) return validYear(+m[1]) ? { year: +m[1], month: null } : null;
  // Two-digit years ("Aug 24", "Aug '24", "08/24", "'19"): this century unless that's in the future.
  const fullYear = (yy: string) => {
    const n = +yy;
    return n <= (now.getFullYear() % 100) + 1 ? 2000 + n : 1900 + n;
  };
  m = text.match(/^([a-z]{3,9})\.?,?\s*['’]?(\d{2})$/);
  if (m && MONTHS.indexOf(m[1].slice(0, 3)) >= 0) {
    const year = fullYear(m[2]);
    return validYear(year) ? { year, month: MONTHS.indexOf(m[1].slice(0, 3)) + 1 } : null;
  }
  m = text.match(/^(\d{1,2})[/.-](\d{2})$/);
  if (m && +m[1] >= 1 && +m[1] <= 12) {
    const year = fullYear(m[2]);
    return validYear(year) ? { year, month: +m[1] } : null;
  }
  m = text.match(/^['’](\d{2})$/);
  if (m) {
    const year = fullYear(m[1]);
    return validYear(year) ? { year, month: null } : null;
  }
  // "1 Dec 2021", "December 1, 2021": a month name is written, so the built-in parser can be trusted.
  // Only with a four-digit year: without one the built-in parser invents 2001 ("Aug 24").
  if (/\b\d{4}\b/.test(text) && MONTHS.some((name) => new RegExp(`\\b${name}`).test(text))) {
    const parsed = new Date(String(value));
    if (!isNaN(parsed.getTime()) && validYear(parsed.getFullYear())) {
      return { year: parsed.getFullYear(), month: parsed.getMonth() + 1 };
    }
  }
  // "Since 2019", "Summer 2019", "2019 onwards": only the year is known. (The built-in parser would
  // read these as January, a month the resume never gave.)
  const year = text.match(/\b(19[5-9]\d|20\d\d)\b/);
  return year && validYear(+year[1]) ? { year: +year[1], month: null } : null;
}

/** A single resume date's parts (month 1-12; year null for a bare month), or null. */
export function parseDateParts(
  value: unknown,
  now: Date = new Date(),
): { year: number | null; month: number | null } | null {
  return parseLoose(value, now);
}

/** A single resume date as a month and year, or null when it isn't one (or has no year). */
export function parseMonthYear(value: unknown, now: Date = new Date()): MonthYear | null {
  const d = parseLoose(value, now);
  return d && d.year != null ? { year: d.year, month: d.month } : null;
}

/** True for "Present", "current", "now" and the like. */
export function isOngoing(value: unknown): boolean {
  return typeof value === "string" && ONGOING.test(value.trim());
}

/**
 * Splits a free-text range ("Jan 2020 – Present", "03/2019 - 05/2021") into its two ends. A single
 * date is both start and end.
 */
export function splitDateRange(text: unknown): { startDate?: string; endDate?: string } {
  if (typeof text !== "string" || !text.trim()) return {};
  const cleaned = text
    .trim()
    .replace(/\s*\([^)]*\)\s*$/, "")
    .replace(/\s*[·•|]\s*\d.*$/, "");
  const parts = cleaned.split(/\s+(?:–|—|-|to|until)\s+|\s*[–—]\s*/i).filter(Boolean);
  const [start, end] = parts;
  if (!end) return { startDate: start, endDate: start };
  return {
    startDate: start,
    endDate: /present|current|now|till date|ongoing/i.test(end) ? "Present" : end,
  };
}

const validMonthYear = (v: unknown): v is MonthYear =>
  !!v &&
  typeof v === "object" &&
  Number.isInteger((v as MonthYear).year) &&
  ((v as MonthYear).month == null ||
    (Number.isInteger((v as MonthYear).month) &&
      (v as MonthYear).month! >= 1 &&
      (v as MonthYear).month! <= 12));

/** True when `value` is stored dates this reader can trust. */
export function isExperienceDates(value: unknown): value is ExperienceDates {
  if (!value || typeof value !== "object") return false;
  const d = value as ExperienceDates;
  return (
    (d.start === null || validMonthYear(d.start)) &&
    (d.end === null || validMonthYear(d.end)) &&
    typeof d.current === "boolean"
  );
}

/**
 * Dates read from what a resume wrote: separate startDate/endDate strings (theRecruit's parsed
 * resumes) or one free-text `date` (theResume). A start written as a bare month ("March – May 2026")
 * takes the end's year, or the year before when its month is later.
 */
export function datesFromText(
  entry: { date?: unknown; startDate?: unknown; endDate?: unknown },
  now: Date = new Date(),
): ExperienceDates | null {
  const fromText =
    entry.startDate == null || entry.startDate === "" ? splitDateRange(entry.date) : {};
  const rawStart = entry.startDate ?? fromText.startDate;
  const rawEnd =
    entry.startDate != null && entry.startDate !== "" ? entry.endDate : fromText.endDate;
  const current = isOngoing(rawEnd);
  const end = current ? null : parseLoose(rawEnd, now);
  const start = parseLoose(rawStart, now);
  if (!start && !end && !current) return null;
  const endYear = current ? now.getFullYear() : (end?.year ?? null);
  const endMonth = current ? now.getMonth() + 1 : (end?.month ?? null);
  let startYear = start?.year ?? null;
  if (start && startYear == null && endYear != null) {
    startYear =
      start.month != null && endMonth != null && start.month > endMonth ? endYear - 1 : endYear;
  }
  return {
    start: start && startYear != null ? { year: startYear, month: start.month } : null,
    end: end && end.year != null ? { year: end.year, month: end.month } : null,
    current,
    source: "parsed",
  };
}

/** An entry's dates: the stored ones when present and valid, otherwise read from its text. */
export function readExperienceDates(
  entry: Record<string, unknown> | null | undefined,
  now: Date = new Date(),
): ExperienceDates | null {
  if (!entry) return null;
  const stored = entry["dates"];
  if (isExperienceDates(stored)) return stored;
  return datesFromText(entry, now);
}

const sameMonthYear = (a: MonthYear | null, b: MonthYear | null): boolean =>
  a === null || b === null
    ? a === b
    : a.year === b.year && (a.month === null || b.month === null || a.month === b.month);

/**
 * Dates for a role read from an uploaded resume. The parser returns the dates as written
 * (startDate/endDate) and its own structured reading (`dates`). What the resume says wins when it
 * can be read; the structured reading only fills what the text reader couldn't ("Since 2019",
 * "Summer 2019") and never adds a month the text doesn't give. `conflict` is true when the two
 * readings disagree, for logging.
 */
export function reconcileParsedDates(
  entry: { date?: unknown; startDate?: unknown; endDate?: unknown; dates?: unknown },
  now: Date = new Date(),
): { dates: ExperienceDates | null; conflict: boolean } {
  const fromText = datesFromText(entry, now);
  const raw = entry.dates as Partial<ExperienceDates> | null | undefined;
  const model =
    raw && typeof raw === "object" && typeof raw.current === "boolean"
      ? {
          start: validMonthYear(raw.start)
            ? { year: raw.start.year, month: raw.start.month ?? null }
            : null,
          end: raw.current
            ? null
            : validMonthYear(raw.end)
              ? { year: raw.end.year, month: raw.end.month ?? null }
              : null,
          current: raw.current,
        }
      : null;
  if (!fromText && !model) return { dates: null, conflict: false };
  if (!fromText)
    return { dates: model!.start ? { ...model!, source: "parsed" } : null, conflict: false };
  if (!model) return { dates: fromText, conflict: false };
  const conflict =
    fromText.current !== model.current ||
    !sameMonthYear(fromText.start, model.start) ||
    (!fromText.current && !sameMonthYear(fromText.end, model.end));
  return {
    dates: {
      start: fromText.start ?? model.start,
      end: fromText.end ?? (fromText.current ? null : model.end),
      current: fromText.current || (!fromText.end && model.current),
      source: "parsed",
    },
    conflict,
  };
}

/** Months since year 0, counting a year-only date from January. */
export const monthIndex = (d: MonthYear): number => d.year * 12 + (d.month ?? 1) - 1;

const fromIndex = (i: number): MonthYear => ({ year: Math.floor(i / 12), month: (i % 12) + 1 });

/**
 * Every entry's dates with the end of each role worked out: a role with no end date ends where the
 * next role starts (inferred); the latest such role is missing an end (it may or may not be
 * current). Same order as the input; null for an entry with no readable start.
 */
export function resolveTimeline(
  entries: Array<Record<string, unknown> | null | undefined>,
  now: Date = new Date(),
): Array<ResolvedDates | null> {
  const dates = entries.map((e) => readExperienceDates(e, now));
  const starts = dates.map((d) => (d?.start ? monthIndex(d.start) : null));
  return dates.map((d, i) => {
    if (!d?.start) return null;
    const precision: ResolvedDates["precision"] =
      d.start.month == null || (d.end != null && d.end.month == null) ? "year" : "month";
    if (d.current) return { ...d, endStatus: "current", precision };
    if (d.end) return { ...d, endStatus: "given", precision };
    const later = starts.filter((s): s is number => s != null && s > starts[i]!);
    if (!later.length) return { ...d, endStatus: "missing", precision };
    return { ...d, end: fromIndex(Math.min(...later)), endStatus: "inferred", precision };
  });
}

/**
 * Years worked across entries, overlaps counted once (a side job during a full-time one), to one
 * decimal. A current role counts up to today. The latest role with no end date counts as current
 * unless `missingEnd: "exclude"`, which counts only what the resume proves.
 */
export function experienceYears(
  entries: Array<Record<string, unknown> | null | undefined>,
  options: {
    now?: Date;
    missingEnd?: "current" | "exclude";
    include?: (index: number) => boolean;
  } = {},
): number {
  const now = options.now ?? new Date();
  const today = now.getFullYear() * 12 + now.getMonth();
  const intervals: Array<[number, number]> = [];
  resolveTimeline(entries, now).forEach((d, i) => {
    if (!d?.start || (options.include && !options.include(i))) return;
    if (d.endStatus === "missing" && options.missingEnd === "exclude") return;
    const from = monthIndex(d.start);
    const to = d.endStatus === "current" || d.endStatus === "missing" ? today : monthIndex(d.end!);
    if (to > from) intervals.push([from, to]);
  });
  intervals.sort((a, b) => a[0] - b[0]);
  let months = 0;
  let open: [number, number] | null = null;
  for (const [from, to] of intervals) {
    if (open && from <= open[1]) open[1] = Math.max(open[1], to);
    else {
      if (open) months += open[1] - open[0];
      open = [from, to];
    }
  }
  if (open) months += open[1] - open[0];
  return Math.round((months / 12) * 10) / 10;
}

const formatOne = (d: MonthYear) =>
  d.month ? `${MONTH_NAMES[d.month - 1]} ${d.year}` : `${d.year}`;

/** "Mar 2019 - Present", "Mar 2019 - Jun 2021", "2019 - 2021", as LinkedIn shows them. */
export function formatExperienceDates(
  dates: Pick<ExperienceDates, "start" | "end" | "current">,
): string {
  if (!dates.start) return "";
  const end = dates.current ? "Present" : dates.end ? formatOne(dates.end) : "";
  return end ? `${formatOne(dates.start)} - ${end}` : formatOne(dates.start);
}

/** A string form for fields that keep text ("2019-03", "2019"), e.g. startDate/endDate. */
export const monthYearText = (d: MonthYear): string =>
  d.month ? `${d.year}-${String(d.month).padStart(2, "0")}` : `${d.year}`;

/** Elasticsearch mapping for a stored `dates` object, so years and months are numbers, not guesses. */
export const EXPERIENCE_DATES_MAPPING = {
  properties: {
    start: { properties: { year: { type: "short" }, month: { type: "byte" } } },
    end: { properties: { year: { type: "short" }, month: { type: "byte" } } },
    current: { type: "boolean" },
    source: { type: "keyword" },
  },
} as const;

/**
 * Marks a stored resume whose jobs carry `dates` worked out with these rules (written by every
 * resume writer and by the backfill). Bump the version when the rules change to backfill again.
 */
export const EXPERIENCE_DATES_FIELD = "experience_dates_version";
/** 1: structured dates on every job. 2: plus the employment summary fields. */
export const EXPERIENCE_DATES_VERSION = 2;

/**
 * employed: a job is current. not_employed: every job has ended. unknown: the latest job has no end
 * date, so it may or may not be current (never guessed either way).
 */
export type EmploymentStatus = "employed" | "not_employed" | "unknown";

/** Summary fields stored on every resume, for filters ("currently employed", "time in current job"). */
export const EMPLOYMENT_FIELDS_MAPPING = {
  employment_status: { type: "keyword" },
  current_job_start: { type: "date", format: "yyyy-MM||yyyy" },
} as const;

/**
 * Whether the person is employed now, and when the current job started ("2019-03", or "2019" when
 * only the year is known). With several current jobs, the one started most recently.
 */
export function employmentSummary(
  entries: Array<Record<string, unknown> | null | undefined>,
  now: Date = new Date(),
): { employment_status: EmploymentStatus; current_job_start: string | null } | null {
  const timeline = resolveTimeline(entries, now).filter((d): d is ResolvedDates => !!d?.start);
  if (!timeline.length) return null;
  const current = timeline.filter((d) => d.endStatus === "current");
  if (current.length) {
    const latest = current.reduce((a, b) => (monthIndex(b.start!) > monthIndex(a.start!) ? b : a));
    return { employment_status: "employed", current_job_start: monthYearText(latest.start!) };
  }
  if (timeline.some((d) => d.endStatus === "missing")) {
    return { employment_status: "unknown", current_job_start: null };
  }
  return { employment_status: "not_employed", current_job_start: null };
}

/** The summary fields to write on a resume document; empty values when there are no dated jobs. */
export function employmentFields(
  entries: Array<Record<string, unknown> | null | undefined> | null | undefined,
  now: Date = new Date(),
): { employment_status: EmploymentStatus | null; current_job_start: string | null } {
  const summary = Array.isArray(entries) ? employmentSummary(entries, now) : null;
  return {
    employment_status: summary?.employment_status ?? null,
    current_job_start: summary?.current_job_start ?? null,
  };
}
